// Region SEO helpers: self-canonical + full reciprocal hreflang set.
// Client-safe (no server-only imports).

import { DEFAULT_REGION, REGIONS, pathWithRegion, isCommercePath } from './regions'

// Reciprocal hreflang map. Every region page emits the SAME complete set
// (self-referencing included), so the set is authoritative regardless of which
// region is currently rendered.
//
//   en-US / en-CA                                 -> us   (curated)
//   en-AU / en-NZ                                 -> au   (curated)
//   en-GB + curated en-DE/en-FR/en-IT/en-ES/en-NL -> eu   (curated)
//   en-<CC> for each served world country          -> world (BACKEND-DRIVEN)
//   en (language-only)                            -> eu
//   x-default                                     -> us
//
// LANGUAGE-VS-COUNTRY: the scheme is LANGUAGE-keyed (en-<CC>) because every store
// serves an English-language site — the <CC> is a REGION targeting hint, not a
// translation. us/eu/au stay CURATED here. The /world alternates are NO LONGER a
// hardcoded curated subset: they are BACKEND-SERVED-DRIVEN, built dynamically
// from `servedWorldCountries` (the sorted list of `store==='world' && served`
// ISO codes from the country map; see countryStore.servedWorldCountriesFromMap),
// emitting one en-<CC> per served world country. regionSeo stays CLIENT-SAFE:
// the list is passed in as a param, never imported. Each store's hreflang is
// gated by `servedStores` (item 4): a store with NO served countries emits no
// alternates; x-default (-> us) is always kept.
//
// CURATED (us/eu/au) rows only. The world rows are generated per-request from
// `servedWorldCountries` inside regionSeoProps (see worldAlternateRows).
const HREFLANG_MAP = [
  { hrefLang: 'en-US', region: 'us' },
  { hrefLang: 'en-CA', region: 'us' },
  { hrefLang: 'en-AU', region: 'au' },
  { hrefLang: 'en-NZ', region: 'au' },
  { hrefLang: 'en-GB', region: 'eu' },
  { hrefLang: 'en-DE', region: 'eu' },
  { hrefLang: 'en-FR', region: 'eu' },
  { hrefLang: 'en-IT', region: 'eu' },
  { hrefLang: 'en-ES', region: 'eu' },
  { hrefLang: 'en-NL', region: 'eu' },
  { hrefLang: 'en', region: 'eu' },
  { hrefLang: 'x-default', region: DEFAULT_REGION },
]

// Cold-boot/fallback world set: used ONLY when the backend served-world list is
// absent (empty/undefined — e.g. a cold boot before the country map loads). We
// emit a small, known-good curated set so we never emit a broken/empty world
// hreflang block while still pointing crawlers at the canonical /world URL. The
// LIVE path always passes the full backend list, which supersedes this.
const FALLBACK_WORLD_COUNTRIES = ['NO', 'CH', 'IS', 'JP', 'KR', 'SG', 'AE']

// Build the { hrefLang: 'en-<CC>', region: 'world' } rows for the served world
// countries (sorted, deterministic → byte-stable HTML). Falls back to the
// curated set when the backend list is empty/undefined.
function worldAlternateRows(servedWorldCountries) {
  const list =
    Array.isArray(servedWorldCountries) && servedWorldCountries.length
      ? servedWorldCountries
      : FALLBACK_WORLD_COUNTRIES
  return list.map((cc) => ({ hrefLang: `en-${String(cc).toUpperCase()}`, region: 'world' }))
}

// Build the absolute URL for a region + region-less path.
// The region prefix is ALWAYS included (every region, incl. the default 'us').
function regionUrl(baseUrl, region, path) {
  return `${baseUrl}${pathWithRegion(path || '/', region)}`
}

/**
 * @param baseUrl              config.url (no trailing slash)
 * @param region               currently-rendered region ('us'|'eu'|'au'|'world')
 * @param path                 region-LESS slug/path ('shop' / '/shop' / '/')
 * @param servedStores         optional Set/array of stores that have >=1 served
 *                             country. hreflang for a store is emitted ONLY when
 *                             the store is served (item 4: no hreflang for
 *                             served=false). Defaults to ALL indexed regions
 *                             (every store currently has served countries).
 *                             x-default (-> us) is always kept.
 * @param servedWorldCountries optional SORTED array of ISO codes that are in the
 *                             /world store AND served (from the backend country
 *                             map). The /world alternates are built dynamically
 *                             as en-<CC> from this list (us/eu/au stay curated).
 *                             Empty/undefined -> a small curated fallback world
 *                             set so we never emit a broken/empty world block.
 * @returns { canonical, languageAlternates } for NextSeo
 */
export function regionSeoProps(baseUrl, region, path, servedStores, servedWorldCountries) {
  if (!baseUrl) {
    return { canonical: undefined, languageAlternates: [] }
  }

  const rel = String(path || '').replace(/^\/+/, '')
  const normalizedPath = rel ? `/${rel}` : '/'

  const servedSet =
    servedStores instanceof Set
      ? servedStores
      : new Set(Array.isArray(servedStores) ? servedStores : REGIONS)

  // Curated us/eu/au/en/x-default rows + the backend-driven world rows.
  const rows = HREFLANG_MAP.concat(worldAlternateRows(servedWorldCountries))

  return {
    // self-canonical: the region's own URL canonicalises to itself.
    canonical: regionUrl(baseUrl, region, normalizedPath),
    languageAlternates: rows
      // Keep x-default always; otherwise only emit alternates for SERVED stores.
      .filter(({ hrefLang, region: r }) => hrefLang === 'x-default' || servedSet.has(r))
      .map(({ hrefLang, region: r }) => ({
        hrefLang,
        href: regionUrl(baseUrl, r, normalizedPath),
      })),
  }
}

/**
 * SEO props for a CONTENT page. Content lives at a single BARE URL (region
 * prefixes 301 to bare), so it self-canonicalises to that bare URL and emits NO
 * hreflang (one URL, no regional variants).
 *
 * @param baseUrl  config.url (no trailing slash)
 * @param path     region-less slug/path ('about-us', '/about-us', '/' or '')
 */
export function contentSeoProps(baseUrl, path) {
  if (!baseUrl) {
    return { canonical: undefined, languageAlternates: [] }
  }
  const rel = String(path || '').replace(/^\/+|\/+$/g, '')
  return {
    canonical: rel ? `${baseUrl}/${rel}` : baseUrl,
    languageAlternates: [],
  }
}

/**
 * Pick the right SEO props for a route by its kind:
 *   - COMMERCE -> region-prefixed self-canonical (/us|/eu|/au) + full hreflang.
 *   - CONTENT  -> single bare canonical, no hreflang.
 * This keeps canonicals and the sitemap pointed at 200-OK URLs only (never the
 * bare commerce URL, which 307-redirects, nor /eu|/au content URLs, which 308).
 */
export function seoPropsForRoute(baseUrl, region, path, servedStores, servedWorldCountries) {
  return isCommercePath(path)
    ? regionSeoProps(baseUrl, region, path, servedStores, servedWorldCountries)
    : contentSeoProps(baseUrl, path)
}
